import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useGameUrlMutation } from '@/features/game-api/use-game-url-mutation/use-game-url-mutation'
import { useWalletStore } from '@/features/wallet-store/use-wallet-store'
import { SystemClock } from '@/features/system-clock/system-clock'
import {
  createFileRoute,
  notFound,
  useCanGoBack,
  useLocation,
} from '@tanstack/react-router'
import { GamePlayRootLayout } from '../../-game-play-root-layout'
import { cn } from '@vgw/tailwind-merger'
import { useGameMessageHandler } from '@/features/gameplay/use-game-message-handler/use-game-message-handler'
import { BrandedLoadingAnimation } from '@/features/gameplay/branded-loading-animation/branded-loading-animation'
import { GamePlayError } from '@/features/gameplay/game-play-error'
import { GamePlayGeoBlockError } from '@/features/gameplay/game-play-geo-block-error'
import { assertCanPlayBeforeLoad } from '@/features/ctrls-and-caps/assert-can-play-before-load'
import { useGameAccessAttemptedTracking } from '@/features/gameplay/use-game-access-attempted-tracking/use-game-access-attempted-tracking'
import { useBrandGamesQuery } from '@/features/games-api/use-brand-games-query/use-brand-games-query'
import { useGamePageTracking } from '@/features/gameplay/use-game-page-tracking/use-game-page-tracking'
import { OutOfTokensDrawer } from '@/features/out-of-tokens/out-of-tokens-drawer/out-of-tokens-drawer'
import { ActivityReminder } from '@/features/activity-reminder/activity-reminder'
import { getBrandAssetsConfig } from '@/config/assets-config'
import { useQueryClient } from '@tanstack/react-query'
import { getFreeGcConfigurationOptions } from '@/apis/pam-player-tokens-api'
import { useGameAudioControl } from '@/features/gameplay/use-game-audio-control/use-game-audio-control'
import { useBroadcastMessage } from '@/features/broadcast-channel/use-broadcast-message'
import { useGameRestartSubscribe } from '@/features/game/use-game-restart-subscribe/use-game-restart-subscribe'
import { coinsStatePollingController } from '@/features/coins-state/use-coins-state-query/coins-state-polling-controller'
import { useFeatureFlag } from '@/lib/use-feature-flag'
import { GamePlayUnsupportedCoinTypeError } from '@/features/gameplay/game-play-unsupported-coin-type-error'

export const Route = createFileRoute('/_app/game-play/$gameId')({
  component: GamePlay,
  beforeLoad: async ({ context }) => {
    return assertCanPlayBeforeLoad(context.queryClient)
  },
})

function GamePlay() {
  const { gameId } = Route.useParams()
  const queryClient = useQueryClient()
  const { backgroundImages } = getBrandAssetsConfig()
  const canGoBack = useCanGoBack()
  const location = useLocation()
  const { launchedGame: preLaunchedGame } = location.state || {}

  const { enabled: isNovaRibbonFeatureFlagEnabled } = useFeatureFlag({
    flagKey: 'nova-release-ribbon-tokens-prompt',
  })

  const coinType = useWalletStore((state) => state.coinType)

  const iframeRef = useRef<HTMLIFrameElement>(null)
  const hasTrackedRef = useRef(false)
  const [isOutOfTokensDrawerOpen, setIsOutOfTokensDrawerOpen] = useState(false)

  const {
    data: games,
    error: brandGamesError,
    isFetched: isBrandGamesFetched,
  } = useBrandGamesQuery({ coinType })

  const {
    reset: resetGameUrlMutation,
    data: fetchedLaunchedGame,
    error: launchedGameError,
    mutate: getGameUrlMutation,
  } = useGameUrlMutation()

  const launchedGame = preLaunchedGame || fetchedLaunchedGame

  const game = useMemo(
    () => games?.content.find((game) => game.gameId === gameId),
    [games, gameId],
  )

  useGamePageTracking({
    gameId,
    gameName: game?.name,
  })
  const { trackGameCardAccessed } = useGameAccessAttemptedTracking()

  const {
    hasError: hasGenericGameLaunchError,
    isGameLoaded,
    isGameLoading,
    hasGeoBlockError: hasGeoBlockGameMessageError,
    onGameReinitialized,
  } = useGameMessageHandler({
    onInSufficientCoinsHandle: () => setIsOutOfTokensDrawerOpen(true),
  })
  useGameAudioControl({ iframeRef, isGameLoaded })

  const onResetGame = useCallback(() => {
    if (!game) {
      return
    }
    resetGameUrlMutation()
    onGameReinitialized()
    getGameUrlMutation({
      launchUrl: game.launchUrl,
      selectedCoinType: coinType,
    })
  }, [
    game,
    coinType,
    getGameUrlMutation,
    resetGameUrlMutation,
    onGameReinitialized,
  ])

  const { broadcast: broadcastReinitializeGame } = useBroadcastMessage({
    channel: 'reinitialize_game_channel',
    message: 'reinitialize_game',
    onMessage: useCallback(() => {
      setIsOutOfTokensDrawerOpen(false)
      onResetGame()
    }, [onResetGame]),
  })

  useGameRestartSubscribe({
    subscribe: useCallback(() => {
      onResetGame()
      broadcastReinitializeGame()
    }, [onResetGame, broadcastReinitializeGame]),
  })

  useEffect(() => {
    queryClient.prefetchQuery({ ...getFreeGcConfigurationOptions() })

    if (isNovaRibbonFeatureFlagEnabled) {
      coinsStatePollingController.suspend(queryClient)
    }

    return () => {
      if (isNovaRibbonFeatureFlagEnabled) {
        coinsStatePollingController.resume(queryClient)
      }
    }
  }, [queryClient, isNovaRibbonFeatureFlagEnabled])

  useEffect(() => {
    if (preLaunchedGame) {
      return
    }

    if (!isBrandGamesFetched || brandGamesError) {
      return
    }

    if (isBrandGamesFetched && !game) {
      throw notFound()
    }

    if (!game) {
      return
    }

    getGameUrlMutation({
      launchUrl: game.launchUrl,
      selectedCoinType: coinType,
    })
  }, [
    coinType,
    getGameUrlMutation,
    game,
    isBrandGamesFetched,
    brandGamesError,
    preLaunchedGame,
  ])

  useEffect(() => {
    if (!canGoBack && game?.name && !hasTrackedRef.current) {
      hasTrackedRef.current = true
      trackGameCardAccessed({
        gameId,
        gameName: game.name,
        gameEntryPoint: 'deeplink',
      })
    }
  }, [trackGameCardAccessed, gameId, game?.name, canGoBack])

  const hasGeoBlockError = useMemo(() => {
    const hasGeoBlockLaunchedGameError =
      !!launchedGameError?.details?.errors?.find(
        ({ errorName }) => errorName === 'BlockedByGeofencing',
      )

    return hasGeoBlockLaunchedGameError || hasGeoBlockGameMessageError
  }, [launchedGameError, hasGeoBlockGameMessageError])

  const hasUnsupportedCoinTypeError = launchedGameError?.details?.errors?.find(
    ({ errorName }) => errorName === 'UnsupportedCoinType',
  )

  if (hasGeoBlockError) {
    return (
      <GamePlayRootLayout>
        <div
          className="flex w-full flex-1 flex-col bg-contain bg-top bg-no-repeat 2xl:bg-cover"
          style={{
            backgroundImage: `url(${backgroundImages.smallBackground})`,
          }}
        >
          <GamePlayGeoBlockError />
        </div>
      </GamePlayRootLayout>
    )
  }

  if (hasUnsupportedCoinTypeError) {
    return (
      <GamePlayRootLayout>
        <div
          className="flex w-full flex-1 flex-col bg-contain bg-top bg-no-repeat 2xl:bg-cover"
          style={{
            backgroundImage: `url(${backgroundImages.smallBackground})`,
          }}
        >
          <GamePlayUnsupportedCoinTypeError />
        </div>
      </GamePlayRootLayout>
    )
  }

  if (brandGamesError || launchedGameError || hasGenericGameLaunchError) {
    return (
      <GamePlayRootLayout>
        <div
          className="flex w-full flex-1 flex-col bg-contain bg-top bg-no-repeat 2xl:bg-cover"
          style={{
            backgroundImage: `url(${backgroundImages.smallBackground})`,
          }}
          data-testid="game-play-error"
        >
          <GamePlayError />
        </div>
      </GamePlayRootLayout>
    )
  }

  return (
    <GamePlayRootLayout mainClassName="bg-black">
      <div className="relative flex h-dvh w-full flex-1">
        {launchedGame && !launchedGame.providesClock ? (
          <SystemClock className="absolute top-0.5 left-0.5" />
        ) : null}
        {launchedGame?.launchUrl ? (
          <>
            <ActivityReminder />
            <iframe
              ref={iframeRef}
              title="Game"
              className={cn('flex-1 transition-opacity duration-500', {
                'opacity-0': isGameLoading,
                'opacity-100': !isGameLoading,
              })}
              src={launchedGame.launchUrl}
              data-testid="game-play-iframe"
            />
          </>
        ) : null}
        {isGameLoading ? (
          <BrandedLoadingAnimation className="absolute top-1/2 left-1/2 size-24 -translate-x-1/2 -translate-y-1/2" />
        ) : null}
      </div>
      <OutOfTokensDrawer
        open={isOutOfTokensDrawerOpen}
        onClose={() => setIsOutOfTokensDrawerOpen(false)}
      />
    </GamePlayRootLayout>
  )
}
